"use client";

/**
 * AlertViewSwitcher — tab strip below AlertTopBar on /alerts/[id].
 *
 * Three views:
 *   Dashboard   → typed result (signal perf / HTML dashboard — built later)
 *   Description → markdown explainer (built later)
 *   Canvas      → existing node engine (AlertCanvas)
 *
 * Active tab is synced to ?tab=dashboard|description|canvas (default: dashboard).
 */

import { useCallback } from "react";
import { useRouter, useSearchParams, usePathname } from "next/navigation";
import { LayoutDashboard, FileText, GitBranch } from "lucide-react";

export type AlertViewTab = "dashboard" | "description" | "canvas";

const TABS: { key: AlertViewTab; label: string; Icon: typeof LayoutDashboard }[] = [
  { key: "dashboard", label: "Dashboard", Icon: LayoutDashboard },
  { key: "description", label: "Description", Icon: FileText },
  { key: "canvas", label: "Canvas", Icon: GitBranch },
];

export function parseAlertViewTab(raw: string | null): AlertViewTab {
  if (raw === "dashboard" || raw === "description" || raw === "canvas") return raw;
  return "dashboard";
}

export function AlertViewSwitcher({ active, viewMode }: { active: AlertViewTab; viewMode?: string }) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const setTab = useCallback(
    (tab: AlertViewTab) => {
      const params = new URLSearchParams(searchParams?.toString() ?? "");
      if (tab === "dashboard") {
        params.delete("tab");
      } else {
        params.set("tab", tab);
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname, { scroll: false });
    },
    [router, pathname, searchParams],
  );

  return (
    <div className="flex items-center p-0.5 border border-border/60 bg-slate-900/60 rounded-full shrink-0">
      {TABS.map(({ key, label, Icon }) => (
        <button
          key={key}
          type="button"
          onClick={() => setTab(key)}
          title={label}
          className={[
            "inline-flex items-center gap-1.5 h-6 px-2 md:px-3 rounded-full text-[11px] font-semibold tracking-wide transition-colors",
            active === key
              ? viewMode === "signal" ? "bg-emerald-500/90 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset]" : "bg-sky-500/90 text-white shadow-[0_1px_0_0_rgba(255,255,255,0.15)_inset]"
              : "text-slate-300 hover:text-slate-100 hover:bg-slate-800/60",
          ].join(" ")}
        >
          <Icon className="w-3.5 h-3.5" />
          <span className="hidden md:inline">{label}</span>
        </button>
      ))}
    </div>
  );
}
